import { useEffect, useState } from 'react';
import { Button, Footer } from 'src/components';
import { useOnboardingContext } from '../context';
import StepBar from '../components/StepBar';
import { useLoadDemoSamplesMutation } from 'src/api/mutations/onboardingMutations';
import { useUpdateOnboardingProgressMutation } from 'src/api/mutations/onboardingMutations';
import { useAlerts } from 'src/common/AlertManager';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import AIWorkflowGuide, { InAppAgentRun } from 'src/components/AIWorkflowGuide';
import { useAiAgentAvailable } from 'src/hooks/useAiAgentAvailable';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { getSearch } from 'src/api/functions/assetsApi';
import styles from '../styles.module.scss';

type Phase = 'opt-in' | 'loading' | 'verifying-error' | 'guide';

const SAMPLE_KEYWORD = 'sample';
const REQUIRED_COUNT = 5;
const POLL_INTERVAL_MS = 5000;
const MAX_ATTEMPTS = 3;

async function countSampleAssets(token: string): Promise<number> {
  try {
    const res = (await getSearch(
      {
        page: 1,
        face: undefined,
        hash: '',
        limit_per_page: 1,
        keyword: SAMPLE_KEYWORD,
        extended_result: false,
        sort_by: [],
        content_type: '',
        file_name: '',
        upload_time_range: {},
        status: '',
      } as any,
      token,
    )) as any;
    return res?.total_files ?? 0;
  } catch {
    return 0;
  }
}

const DemoMode = () => {
  const {
    goToNextStep, goToPreviousStep, userEmail, industryHint, role, setDemoCompleted, setDemoUsedInAppAgent,
    samplesLoaded,
  } = useOnboardingContext();
  const { showAlert } = useAlerts();
  const { getErrorMessage } = useErrorMessages();
  const { inAppAvailable } = useAiAgentAvailable(role);
  const accessToken = useAccessToken();

  const [phase, setPhase] = useState<Phase>('opt-in');
  const [verifying, setVerifying] = useState(false);

  const progressMutation = useUpdateOnboardingProgressMutation();
  const demoMutation = useLoadDemoSamplesMutation({
    onSuccess: () => {
      progressMutation.mutate({ checklist: { demo_loaded: true } });
      setVerifying(true);
    },
    onError: (error) => {
      showAlert(getErrorMessage(error.message));
      setPhase('opt-in');
    },
  });

  const handleStartDemo = () => {
    setPhase('loading');
    if (samplesLoaded) {
      // The Setup step already added them; just wait until they're searchable.
      setVerifying(true);
      return;
    }
    demoMutation.mutate({ industry: industryHint || 'general' });
  };

  const handleRetryVerification = () => {
    setPhase('loading');
    setVerifying(true);
  };

  useEffect(() => {
    if (!verifying) return;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const tick = async (attempt: number) => {
      if (cancelled) return;
      const current = await countSampleAssets(accessToken);
      if (cancelled) return;
      if (current >= REQUIRED_COUNT) {
        setVerifying(false);
        setPhase('guide');
        return;
      }
      if (attempt >= MAX_ATTEMPTS) {
        setVerifying(false);
        setPhase('verifying-error');
        return;
      }
      timeoutId = setTimeout(() => tick(attempt + 1), POLL_INTERVAL_MS);
    };

    timeoutId = setTimeout(() => tick(1), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [verifying, accessToken]);

  const handleFinish = () => {
    goToNextStep();
  };

  return (
    <main className={styles.step}>
      <StepBar
        onGoToNextStep={handleFinish}
        onGoToPreviousStep={goToPreviousStep}
        hideNextButton
      />
      <section className={styles.container}>
        <div className={styles.demoContainer}>
          {phase === 'opt-in' && (
            <>
              <div className={styles.demoHeader}>
                <h2>See FileSpin AI Agent in action</h2>
                <p>
                  {samplesLoaded
                    ? 'Your sample assets are in your library. Try an AI workflow on them right now.'
                    : "We'll add sample assets to your account so you can try an AI workflow right now."}
                  {inAppAvailable
                    ? ' You can run it directly here in under a minute.'
                    : ' Takes about 2 minutes.'}
                </p>
              </div>
              <div className={styles.demoCta}>
                <Button onClick={handleStartDemo} loading={demoMutation.isLoading}>
                  Yes, let's try it
                </Button>
              </div>
            </>
          )}

          {phase === 'loading' && (
            <div className={styles.demoHeader}>
              <h2>Loading sample assets...</h2>
              <p>
                {verifying
                  ? "We're indexing your samples so the AI Agent can find them. This usually takes a few seconds."
                  : "We're adding sample assets to your account. This will only take a moment."}
              </p>
            </div>
          )}

          {phase === 'verifying-error' && (
            <>
              <div className={styles.demoHeader}>
                <h2>Samples are still indexing</h2>
                <p>
                  We added the sample assets but they aren't searchable yet. You can finish
                  onboarding and try the AI Agent any time from the sparkle icon in the dashboard
                  toolbar — it'll work as soon as indexing completes.
                </p>
              </div>
              <div className={styles.demoCta}>
                <Button onClick={handleFinish}>Continue onboarding</Button>
                <Button mode="tertiary" onClick={handleRetryVerification}>
                  Try again
                </Button>
              </div>
            </>
          )}

          {phase === 'guide' && (inAppAvailable ? (
            <InAppAgentRun
              userEmail={userEmail}
              onComplete={() => {
                setDemoCompleted(true);
                setDemoUsedInAppAgent(true);
              }}
              onClose={handleFinish}
            />
          ) : (
            <AIWorkflowGuide
              userEmail={userEmail}
              onComplete={() => setDemoCompleted(true)}
              onClose={handleFinish}
            />
          ))}
        </div>
      </section>
      <Footer />
    </main>
  );
};

export default DemoMode;
